import { motion } from 'framer-motion';
import { SceneFrame } from './_atoms';

/* ------------------------------------------------------------------ */
/*  Architecture diagram — all three layers in one unified view.       */
/*  Inspired by the user-provided dark Eclaims architecture HTML.      */
/*  Terminology: "Category Layer" + "Entity category" throughout.    */
/* ------------------------------------------------------------------ */

function LayerLabel({ children, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
      transition={{ delay }}
      className="w-[110px] flex-shrink-0 pr-3"
    >
      <div className="text-[10px] font-body uppercase tracking-[0.18em] text-slate-400">Layer</div>
      <div className="font-heading font-extrabold text-[15px] leading-tight text-white mt-0.5">{children}</div>
    </motion.div>
  );
}

function ArrowDown({ delay, count = 1, positions = ['50%'] }) {
  return (
    <div className="relative h-6 w-full">
      {positions.map((pos, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scaleY: 0 }} animate={{ opacity: 1, scaleY: 1 }}
          transition={{ delay: delay + i * 0.08, duration: 0.35 }}
          style={{ left: pos }}
          className="absolute top-0 -translate-x-1/2 origin-top"
        >
          <div className="w-[2px] h-4 bg-slate-500" />
          <svg width="10" height="6" viewBox="0 0 10 6" className="-mt-0.5 -ml-1">
            <path d="M1 1 L5 5 L9 1" fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.div>
      ))}
    </div>
  );
}

function StructureLayer() {
  return (
    <div className="grid grid-cols-[1.4fr_1px_1fr] gap-3">
      {/* Organization */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
        className="rounded-md bg-[#1F1A4A] border border-purple-500/40 p-3"
      >
        <span className="inline-block px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 text-[9px] font-body uppercase tracking-wider">organization</span>
        <p className="font-heading font-bold text-[13px] text-white mt-1.5">Organization</p>
        <p className="text-[10.5px] font-body text-slate-400">Parent entity · groups entities</p>
        <div className="grid grid-cols-3 gap-1 mt-2">
          {['Entity 1', 'Entity 2', 'Entity N'].map((f, i) => (
            <motion.div
              key={f}
              initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 + i * 0.1 }}
              className="rounded bg-[#0E2A24] border border-teal-500/30 px-1.5 py-1 text-center"
            >
              <span className="text-[10px] font-body text-teal-300">{f}</span>
            </motion.div>
          ))}
        </div>
        <div className="text-[9.5px] font-body text-slate-400 mt-2 leading-snug">
          → operationally same entity category across all<br />
          → user may be assigned to multiple entities
        </div>
      </motion.div>

      {/* divider */}
      <div className="bg-slate-800 w-px h-full" />

      {/* Standalone */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
        className="rounded-md bg-[#0E2A24] border border-teal-500/40 p-3"
      >
        <span className="inline-block px-1.5 py-0.5 rounded bg-teal-500/20 text-teal-300 text-[9px] font-body uppercase tracking-wider">standalone</span>
        <p className="font-heading font-bold text-[13px] text-white mt-1.5">Standalone Entity</p>
        <p className="text-[10.5px] font-body text-slate-400">Independent · no parent</p>
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8 }}
          className="rounded bg-[#0E2A24] border border-teal-500/40 px-2 py-1.5 text-center mt-2"
        >
          <span className="text-[11px] font-body font-semibold text-teal-300">Entity</span>
        </motion.div>
        <div className="text-[9.5px] font-body text-slate-400 mt-2 leading-snug">
          → user may span multiple standalones<br />
          → same entity category rule enforced
        </div>
      </motion.div>
    </div>
  );
}

function CategoryLayer() {
  return (
    <div>
      <div className="grid grid-cols-[1.5fr_1px_1fr] gap-3">
        {/* Standard categories */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4 }}
          className="rounded-md bg-[#080E18] border border-slate-800 p-3"
        >
          <p className="text-[10px] font-body uppercase tracking-[0.15em] text-slate-300 font-semibold mb-2">Standard categories &nbsp;·&nbsp; scoped access</p>
          <div className="grid grid-cols-[1fr_1fr_0.5fr] gap-2">
            <div className="rounded bg-[#0A1A2E] border border-blue-700/50 px-2.5 py-2">
              <p className="font-heading font-bold text-[11.5px] text-white">Provider</p>
              <p className="text-[9.5px] font-body text-slate-400">own entities only</p>
            </div>
            <div className="rounded bg-[#0A1A2E] border border-blue-700/50 px-2.5 py-2">
              <p className="font-heading font-bold text-[11.5px] text-white">Payer</p>
              <p className="text-[9.5px] font-body text-slate-400">own entities only</p>
            </div>
            <div className="rounded bg-[#0E1320] border border-slate-700 px-2 py-2 flex items-center justify-center opacity-50">
              <span className="text-[12px] font-body text-slate-500">···</span>
            </div>
          </div>
        </motion.div>

        <div className="bg-slate-800 w-px h-full" />

        {/* Cross-entity */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.55 }}
          className="rounded-md bg-[#100604] border border-rose-900/60 p-3"
        >
          <p className="text-[10px] font-body uppercase tracking-[0.15em] text-rose-300/80 font-semibold mb-2">Cross-entity access</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded bg-[#1F0808] border border-rose-700/50 px-2.5 py-2">
              <p className="font-heading font-bold text-[11.5px] text-white">Regulator</p>
              <p className="text-[9.5px] font-body text-rose-300/70">sees all entities</p>
            </div>
            <div className="rounded bg-[#1F0808] border border-rose-700/50 px-2.5 py-2">
              <p className="font-heading font-bold text-[11.5px] text-white">Owner</p>
              <p className="text-[9.5px] font-body text-rose-300/70">sees all entities</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Role catalog bar */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.75 }}
        className="mt-3 rounded-md bg-[#1A1430] border border-indigo-700/40 px-3 py-2 flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <span className="px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 text-[9px] font-body uppercase tracking-wider">role catalog</span>
          <span className="text-[10.5px] font-body text-slate-300">One role set defined per entity category</span>
        </div>
        <span className="text-[10.5px] font-body text-indigo-300">Admin · Reviewer · Clerk · ···</span>
      </motion.div>
    </div>
  );
}

function AssignmentLayer() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.4 }}
      className="flex items-center gap-3 flex-wrap"
    >
      <div className="rounded-md bg-[#0E1320] border border-slate-700 px-4 py-2.5 min-w-[110px]">
        <p className="font-heading font-bold text-[12px] text-white">User</p>
        <p className="text-[9.5px] font-body text-slate-400">single identity</p>
      </div>

      <div className="flex flex-col items-center gap-0.5">
        <span className="text-[8.5px] font-body uppercase tracking-wider text-slate-500">assigned to</span>
        <svg width="44" height="8"><line x1="0" y1="4" x2="36" y2="4" stroke="#475569" strokeWidth="1.2" /><path d="M32 1L40 4L32 7" fill="none" stroke="#94A3B8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>

      <div className="rounded-md bg-[#0A1C18] border border-teal-700/50 px-4 py-2.5 min-w-[110px]">
        <p className="font-heading font-bold text-[12px] text-teal-300">Entity</p>
        <p className="text-[9.5px] font-body text-slate-400">1 of N assigned</p>
      </div>

      <div className="flex flex-col items-center gap-0.5">
        <span className="text-[8.5px] font-body uppercase tracking-wider text-slate-500">holds one</span>
        <svg width="44" height="8"><line x1="0" y1="4" x2="36" y2="4" stroke="#475569" strokeWidth="1.2" /><path d="M32 1L40 4L32 7" fill="none" stroke="#94A3B8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>

      <div className="rounded-md bg-[#1A1208] border border-amber-700/50 px-4 py-2.5 min-w-[110px]">
        <p className="font-heading font-bold text-[12px] text-amber-300">Role</p>
        <p className="text-[9.5px] font-body text-slate-400">from category catalog</p>
      </div>

      <div className="ml-auto space-y-0.5">
        <p className="text-[10px] font-body text-slate-300">→ one role per entity</p>
        <p className="text-[10px] font-body text-slate-300">→ different roles across entities</p>
        <p className="text-[10px] font-body text-slate-300">→ same category across all assignments</p>
      </div>
    </motion.div>
  );
}

export default function ArchThreeLayerScene() {
  return (
    <SceneFrame
      eyebrow="Architecture diagram"
      title="Structure · Category · Assignment"
      subtitle="A single, unified view of how Eclaims governs access — every decision flows through these three layers and converges at User → Entity → Role."
    >
      {/* Dark themed diagram card */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.4 }}
        className="rounded-2xl bg-gradient-to-br from-[#0B1023] via-[#0E1428] to-[#0B0E1F] border border-slate-800 px-6 py-5 shadow-2xl mt-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
          <div>
            <p className="font-heading font-extrabold text-[14px] text-white">Eclaims · User Management Architecture</p>
            <p className="text-[10px] font-body text-slate-400">Access control · Hierarchy model · Role assignment</p>
          </div>
          <span className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-[9.5px] font-body text-slate-300 uppercase tracking-wider">v 1.0</span>
        </div>

        {/* Structure Layer */}
        <div className="flex items-start">
          <LayerLabel delay={0.25}>Structure<br />Layer</LayerLabel>
          <div className="flex-1 min-w-0">
            <StructureLayer />
          </div>
        </div>

        {/* Connecting arrows */}
        <div className="ml-[110px]">
          <ArrowDown delay={1.0} positions={['33%', '78%']} />
        </div>

        {/* Category Layer (was "Type Layer") */}
        <div className="flex items-start">
          <LayerLabel delay={1.2}>Category<br />Layer</LayerLabel>
          <div className="flex-1 min-w-0">
            <CategoryLayer />
          </div>
        </div>

        {/* Connecting arrow */}
        <div className="ml-[110px]">
          <ArrowDown delay={2.05} positions={['50%']} />
        </div>

        {/* Assignment Layer */}
        <div className="flex items-start">
          <LayerLabel delay={2.25}>Assignment<br />Layer</LayerLabel>
          <div className="flex-1 min-w-0">
            <AssignmentLayer />
          </div>
        </div>

        {/* Legend */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.0 }}
          className="mt-5 pt-4 border-t border-slate-800 flex items-center gap-4 flex-wrap"
        >
          {[
            { c: '#3A2880', l: 'Organization' },
            { c: '#1A6050', l: 'Entity' },
            { c: '#143C6A', l: 'Standard category' },
            { c: '#782010', l: 'Cross-entity category' },
            { c: '#2A1F6A', l: 'Role catalog' },
            { c: '#5A3A08', l: 'Role assignment' },
          ].map(it => (
            <div key={it.l} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ background: it.c }} />
              <span className="text-[10px] font-body text-slate-400">{it.l}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </SceneFrame>
  );
}
